import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Components
import Dashboard from "@/components/Dashboard";
import ClientFirewall from "@/components/ClientFirewall";
import Clients from "@/components/Clients";
import Projects from "@/components/Projects";
import MessageHistory from "@/components/MessageHistory";
import Billing from "@/components/Billing";
import BillingSuccess from "@/components/BillingSuccess";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import LandingPage from "@/components/LandingPage";

// Modules
import ProposalEditor from "@/components/ProposalEditor";
import TestimonialFlow from "@/components/TestimonialFlow";
import ScopeGuard from "@/components/ScopeGuard";
import PortalView from "@/components/PortalView";
import SOPGenerator from "@/components/SOPGenerator";
import ColdOutreach from "@/components/ColdOutreach";
import Analytics from "@/components/Analytics";
import EmailTemplates from "@/components/EmailTemplates";

// New Features
import EmailScheduler from "@/components/EmailScheduler";
import Teams from "@/components/Teams";
import Webhooks from "@/components/Webhooks";
import BillingDashboard from "@/components/BillingDashboard";

// V2 Modules
import CampaignBuilder from "@/components/CampaignBuilder";
import ProofBuilder from "@/components/ProofBuilder";
import VideoAdmin from "@/components/VideoAdmin";
import VideoRequestPage from "@/components/VideoRequestPage";
import PublicProofView from "@/components/PublicProofView";
import ContractBuilder from "@/components/ContractBuilder";
import PublicContractView from "@/components/PublicContractView";
import ContractSigningPage from "@/components/ContractSigningPage";
import ContractTemplates from "@/components/ContractTemplates";
import DemoLockModal from "@/components/DemoLockModal";

// V3 Modules - Client Portal & Pricing
import ClientPortal from "@/components/ClientPortal";
import PricingPage from "@/components/PricingPage";
import AdminDashboard from "@/components/AdminDashboard";
import Blog from "@/components/Blog";
import BlogPost from "@/components/BlogPost";

// V3 Growth Features
import AffiliateDashboard from "@/components/AffiliateDashboard";
import BrandingSettings from "@/components/BrandingSettings";
import AdvancedAnalytics from "@/components/AdvancedAnalytics";

// Auth
import AuthPage from "@/components/AuthPage";

// Legal Pages
import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsOfService from "@/components/TermsOfService";

// Settings
import SettingsPage from "@/components/SettingsPage";

// CRM
import CRMSettingsPage from "@/components/CRMSettingsPage";
import CRMReports from "@/components/CRMReports";
import CRMPage from "@/components/CRMPage";

// Migration Pages
import AndCoMigrationPage from "@/components/AndCoMigrationPage";

// Invoice Module
import InvoicesPage from "@/components/InvoicesPage";
import InvoiceDetail from "@/components/InvoiceDetail";
import InvoiceEditor from "@/components/InvoiceEditor";

// Growth Features
import OnboardingChecklist from "@/components/OnboardingChecklist";
import WelcomePage from "@/components/WelcomePage";
import OnboardingTour from "@/components/OnboardingTour";
import RevenueTile from "@/components/RevenueTile";
import NudgeBanner from "@/components/NudgeBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import TemplatesPage from "@/components/TemplatesPage";
import ChangeOrderGenerator from "@/components/ChangeOrderGenerator";
import WinSummary from "@/components/WinSummary";
import Confetti, { useConfetti } from "@/components/Confetti";

// Security
import SecuritySettings from "@/components/SecuritySettings";
import ConnectorSettingsPage from "@/components/ConnectorSettingsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect Safari browser
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// API Helper with credentials
export const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Auth header: attach Clerk JWT when a Clerk session is active.
// (Hooks can't be called outside components, so we use the Clerk window-singleton here.)
api.interceptors.request.use(async (config) => {
  try {
    if (window.Clerk?.session) {
      const clerkToken = await window.Clerk.session.getToken();
      if (clerkToken) {
        config.headers['Authorization'] = `Bearer ${clerkToken}`;
      }
    }
  } catch (e) {
    console.warn("Clerk getToken failed", e);
  }
  return config;
});

export { isSafari };

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!clerkLoaded) return;

    // Reactive sign-out: if Clerk session went away (UserButton sign-out,
    // programmatic signOut), drop FE auth state and redirect.
    if (hasInitialized.current && isAuthenticated && clerkSignedIn === false) {
      hasInitialized.current = false;
      setIsAuthenticated(false);
      setUser(null);
      navigate("/", { replace: true });
      return;
    }

    // Prevent re-running if already authenticated
    if (hasInitialized.current && isAuthenticated) {
      return;
    }

    // Check auth with server. Interceptor attaches Clerk JWT if a session exists.
    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        setUser(response.data);
        setIsAuthenticated(true);
        hasInitialized.current = true;
      } catch (error) {
        console.log("Not authenticated");
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [clerkLoaded, clerkSignedIn, navigate, isAuthenticated]);

  // Fetch usage whenever user changes
  useEffect(() => {
    if (user?.user_id) {
      const fetchUsage = async () => {
        try {
          const usageRes = await api.get(`/users/${user.user_id}/usage`);
          setUsage(usageRes.data);
        } catch (e) {
          console.error("Error fetching usage:", e);
        }
      };
      fetchUsage();
    }
  }, [user]);

  const refreshUsage = async () => {
    if (user?.user_id) {
      try {
        const usageRes = await api.get(`/users/${user.user_id}/usage`);
        setUsage(usageRes.data);
      } catch (e) {
        console.error("Error refreshing usage:", e);
      }
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      await refreshUsage();
    } catch (e) {
      console.error("Error refreshing user:", e);
    }
  };

  // Loading state — also waits for Clerk to load
  if (!clerkLoaded || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Pass props to children
  return children({ user, usage, refreshUsage, refreshUser });
}

// Main App Layout
function AppLayout({ user, usage, refreshUsage, refreshUser }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showWelcomeTutorial, setShowWelcomeTutorial] = useState(false);
  const [demoLocked, setDemoLocked] = useState(false);
  const [demoLockReason, setDemoLockReason] = useState(null);
  // v0.2 Plan 05-04: OnboardingTour step state (1, 2, or 3). null = not in tour.
  const [tourStep, setTourStep] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // v0.2 Plan 05-04: fire welcome email once on first dashboard arrival.
  // BE is idempotent on welcome_email_sent — safe even if this fires twice.
  // Deps deliberately narrow to the two primitives that gate the call;
  // refreshUser closes over the parent and shouldn't drive re-runs here.
  useEffect(() => {
    if (!user || !usage) return;
    if (usage.is_demo) return;
    if (user.welcome_email_sent) return;
    api.post("/users/send-welcome-email")
      .then((res) => {
        if (res.data?.sent || res.data?.reason === "already_sent") {
          refreshUser?.();
        }
      })
      .catch((e) => {
        // Welcome email is nice-to-have. Silent failure — retry on next mount.
        console.warn("Welcome email send failed", e?.message);
      });
  }, [user?.welcome_email_sent, usage?.is_demo]); // eslint-disable-line react-hooks/exhaustive-deps

  // v0.2 Plan 05-04: tour-step initializer.
  // When the user lands on /scope-guard?tutorial=1 with tutorial_completed=false,
  // start the tour at step 1. When they leave that URL, clear tour state so a
  // stale step doesn't render over the wrong screen.
  useEffect(() => {
    const inTutorial =
      location.pathname.startsWith("/scope-guard") &&
      location.search.includes("tutorial=1");
    if (inTutorial && user && user.tutorial_completed === false && tourStep === null) {
      setTourStep(1);
    } else if (!inTutorial && tourStep !== null) {
      setTourStep(null);
    }
  }, [location.pathname, location.search, user?.tutorial_completed, tourStep]);

  // Show tutorial for new users on first load
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem("clientkit_tutorial_completed");
    const hasShownThisSession = sessionStorage.getItem("clientkit_tutorial_shown");
    
    // Show tutorial if: user is new (trial) AND hasn't completed tutorial AND hasn't seen it this session
    if (usage?.is_trial && !tutorialCompleted && !hasShownThisSession) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setShowWelcomeTutorial(true);
        sessionStorage.setItem("clientkit_tutorial_shown", "true");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [usage?.is_trial]);

  // Demo session tracking
  useEffect(() => {
    // Only track for demo users
    if (!usage?.is_demo) return;
    
    const checkDemoStatus = async () => {
      try {
        const res = await api.get("/demo/status");
        if (res.data.is_locked) {
          setDemoLocked(true);
          setDemoLockReason(res.data.lock_reason || "feature_limit");
        }
      } catch (e) {
        // Demo endpoint not available or no session
      }
    };

    // Check immediately
    checkDemoStatus();
    
    // Check every 10 seconds for time limit (more responsive)
    const interval = setInterval(checkDemoStatus, 10000);
    
    return () => clearInterval(interval);
  }, [usage?.is_demo]);

  // Track premium feature usage for demo users
  const trackDemoFeature = async (featureName) => {
    if (!usage?.is_demo) return { allowed: true };
    
    try {
      const res = await api.post("/demo/track-feature", { feature: featureName });
      if (res.data.is_locked) {
        setDemoLocked(true);
        setDemoLockReason(res.data.lock_reason || "feature_limit");
      }
      return res.data;
    } catch (e) {
      return { allowed: true };
    }
  };

  const handleTutorialComplete = () => {
    localStorage.setItem("clientkit_tutorial_completed", "true");
    setShowWelcomeTutorial(false);
  };

  // v0.2 Plan 05-04: OnboardingTour "Got it" handler. Persists tutorial_completed=true,
  // refreshes user so the routing guard sees the new state, then navigates to /dashboard.
  const handleOnboardingTourComplete = async () => {
    try {
      await api.put("/users/me", { tutorial_completed: true });
      await refreshUser?.();
    } catch (e) {
      console.warn("Failed to persist tutorial_completed", e?.message);
    } finally {
      setTourStep(null);
      navigate("/dashboard", { replace: true });
    }
  };

  // Import ProductTutorial dynamically
  const ProductTutorial = require("./components/ProductTutorial").default;

  // v0.2 Plan 05-04: post-Google-auth onboarding redirect — MUST stay AFTER all hooks
  // so the hook-call order is stable across renders. Real users without
  // tutorial_completed get bumped to /welcome. Tutorial-in-progress
  // (/scope-guard?tutorial=1) is exempted so step 2/3 can run inside the layout.
  const onWelcome = location.pathname === "/welcome";
  const inTutorialFlow =
    location.pathname.startsWith("/scope-guard") &&
    location.search.includes("tutorial=1");
  const needsOnboarding =
    user && usage && !usage.is_demo && user.tutorial_completed === false;
  if (needsOnboarding && !onWelcome && !inTutorialFlow) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen} 
          currentPath={location.pathname} 
          onClose={() => setSidebarOpen(false)}
        />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Header */}
          <Header 
            user={user} 
            usage={usage} 
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
          />
          
          {/* Page Content */}
          <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
            <Routes>
              <Route path="/dashboard" element={<Dashboard user={user} usage={usage} />} />
              <Route 
                path="/firewall" 
                element={<ClientFirewall onGenerate={refreshUsage} usage={usage} />} 
              />
              <Route path="/clients" element={<Clients />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/history" element={<MessageHistory />} />
              <Route path="/billing" element={<BillingDashboard user={user} usage={usage} onUpgrade={refreshUser} />} />
              <Route path="/billing/success" element={<BillingSuccess onComplete={refreshUser} />} />
              <Route path="/billing-dashboard" element={<Navigate to="/billing" replace />} />
              
              {/* New Module Routes */}
              <Route path="/projects/:projectId/proposals" element={<ProposalEditor />} />
              <Route path="/projects/:projectId/testimonials" element={<TestimonialFlow />} />
              <Route
                path="/scope-guard"
                element={
                  <ScopeGuard
                    user={user}
                    usage={usage}
                    refreshUsage={refreshUsage}
                    tutorial={inTutorialFlow ? { step: tourStep, onAdvance: setTourStep, useCase: user?.primary_use_case } : null}
                  />
                }
              />
              <Route path="/sop-generator" element={<SOPGenerator user={user} usage={usage} refreshUsage={refreshUsage} />} />
              <Route path="/cold-outreach" element={<ColdOutreach />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/email-templates" element={<EmailTemplates />} />
              
              {/* New Feature Routes */}
              <Route path="/email-scheduler" element={<EmailScheduler />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/webhooks" element={<Webhooks />} />
              
              {/* V2 Module Routes */}
              <Route path="/outreach-pro" element={<CampaignBuilder user={user} />} />
              <Route path="/projects/:projectId/outreach" element={<CampaignBuilder user={user} />} />
              <Route path="/proofs" element={<ProofBuilder user={user} />} />
              <Route path="/projects/:projectId/proofs" element={<ProofBuilder user={user} />} />
              <Route path="/video-testimonials" element={<VideoAdmin user={user} />} />
              <Route path="/projects/:projectId/contract" element={<ContractBuilder user={user} />} />
              <Route path="/contract-templates" element={<ContractTemplates onSelectTemplate={(template) => {
                // Store selected template and navigate to contract builder
                sessionStorage.setItem("selectedContractTemplate", JSON.stringify(template));
                toast.success("Template selected! Choose a project to create a contract.");
              }} />} />
              
              {/* V3 Growth Features */}
              <Route path="/affiliate" element={<AffiliateDashboard />} />
              <Route path="/branding" element={<BrandingSettings />} />
              <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
              
              {/* Settings */}
              <Route path="/settings" element={<SettingsPage user={user} />} />
              <Route path="/settings/security" element={<SecuritySettings />} />
              <Route path="/settings/connector" element={<ConnectorSettingsPage />} />
              
              {/* CRM */}
              <Route path="/crm" element={<CRMPage />} />
              <Route path="/crm/settings" element={<CRMSettingsPage />} />
              <Route path="/crm/reports" element={<CRMReports />} />
              
              {/* Invoices */}
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/invoices/new" element={<InvoiceEditor />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              
              {/* v0.2 Plan 05-04 onboarding: profile capture for new Clerk signups.
                  /onboarding (below) is the legacy OnboardingChecklist feature, unchanged. */}
              <Route path="/welcome" element={<WelcomePage user={user} refreshUser={refreshUser} />} />

              {/* Onboarding */}
              <Route path="/onboarding" element={<OnboardingChecklist />} />
              
              {/* Template Library */}
              <Route path="/templates" element={<TemplatesPage />} />
              
              {/* Change Order Generator */}
              <Route path="/change-orders" element={<ChangeOrderGenerator user={user} usage={usage} refreshUsage={refreshUsage} />} />
              
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
        </div>
      </div>

      {/* Welcome Tutorial for New Users (legacy Help-icon re-trigger) */}
      <ProductTutorial
        isOpen={showWelcomeTutorial}
        onClose={() => setShowWelcomeTutorial(false)}
        onComplete={handleTutorialComplete}
      />

      {/* v0.2 Plan 05-04 OnboardingTour — Scope Guard aha for first-time Clerk signups */}
      {inTutorialFlow && tourStep !== null && (
        <OnboardingTour
          step={tourStep}
          onAdvance={setTourStep}
          onComplete={handleOnboardingTourComplete}
          useCase={user?.primary_use_case}
        />
      )}

      {/* Demo Lock Modal */}
      <DemoLockModal 
        isOpen={demoLocked} 
        lockReason={demoLockReason}
        onClose={() => setDemoLocked(false)}
      />
      
      {/* Feedback Widget */}
      <FeedbackWidget />
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />

      {/* Clerk-mounted sign-in / sign-up — wildcards required for Clerk's internal sub-routes */}
      <Route path="/sign-in/*" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/sign-up/*" element={<PublicRoute><AuthPage /></PublicRoute>} />

      {/* Legal Pages - Public */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Migration Landing Pages - Public */}
      <Route path="/andco-migration" element={<AndCoMigrationPage />} />
      
      {/* Blog - Public, SEO-friendly */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      
      {/* Public Portal - No auth required */}
      <Route path="/portal/:token" element={<PortalView />} />
      
      {/* Public Video Request - No auth required */}
      <Route path="/video/:token" element={<VideoRequestPage />} />
      
      {/* Public Proof/Case Study View - No auth required */}
      <Route path="/proof/:token" element={<PublicProofView />} />
      
      {/* Public Contract View - No auth required */}
      <Route path="/contract/:token" element={<PublicContractView />} />
      
      {/* Public Contract Signing - No auth required */}
      <Route path="/sign/:token" element={<ContractSigningPage />} />
      
      {/* Client Portal - Magic link auth */}
      <Route path="/client-portal" element={<ClientPortal />} />
      
      {/* Secret Admin Dashboard - No links anywhere, access via direct URL only */}
      <Route path="/internal/:adminKey" element={<AdminDashboard />} />
      
      {/* Pricing Page - For new user onboarding */}
      <Route path="/pricing" element={
        <ProtectedRoute>
          {(props) => <PricingPage {...props} onPlanSelected={() => props.refreshUser?.()} />}
        </ProtectedRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/*" element={
        <ProtectedRoute>
          {(props) => <AppLayout {...props} />}
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// Public Route - Redirects to dashboard if already authenticated
function PublicRoute({ children }) {
  const { isLoaded: clerkLoaded } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!clerkLoaded) return;
    const checkAuth = async () => {
      try {
        await api.get("/auth/me");
        setIsAuthenticated(true);
        navigate("/dashboard", { replace: true });
      } catch {
        setIsAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
  }, [clerkLoaded, navigate]);

  if (checking || !clerkLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
